import { useEffect, useMemo, useState } from "react";
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type ColumnDef,
  type OnChangeFn,
  type PaginationState,
  type SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { type TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { Pencil, Trash2, TriangleAlert } from "lucide-react";

import Loading from "@/components/Loading";
import { RelativeDateCell } from "@/components/RelativeDateCell";
import { formatDateTimeCompact, formatRelativeDateTime } from "@/utils/relative-time";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { WFPButton } from "@/components/ui/button";
import { getColumnSortAria, SortableColumnHeader } from "@/components/ui/sortable-column-header";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DataTablePagination } from "@/components/ui/table-pagination";
import { cn } from "@/lib/utils";
import {
  EXTERNAL_USER_TYPE_LABEL_KEYS,
  isExternalUserType,
  type ExternalUser,
  type ExternalUserColumnMeta,
} from "../../types";

interface ListProps {
  isFetching: boolean;
  errorMessage?: string;
  externalUsers: ExternalUser[];
  onClickView: (externalUser: ExternalUser) => void;
  onClickDelete: (externalUser: ExternalUser) => void;
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
}

const PAGE_SIZE = 20;

const EMPTY_VALUE = "-";

export const DEFAULT_SORTING: SortingState = [{ id: "registeredOn", desc: true }];

export const EXTERNAL_USER_SORT_OPTIONS = [
  { id: "registeredOn", translationKey: "column.registeredOn" },
  { id: "type", translationKey: "column.externalUserType" },
  { id: "organization", translationKey: "column.institutionName" },
  { id: "fullName", translationKey: "column.externalUserFullName" },
  { id: "email", translationKey: "column.managerEmail" },
  { id: "lastLogin", translationKey: "column.lastLogin" },
] as const;

const getColumnMeta = (columnDef: ColumnDef<ExternalUser, unknown>) =>
  columnDef.meta as ExternalUserColumnMeta | undefined;

const getTypeLabel = (t: TFunction, type?: string) =>
  isExternalUserType(type) ? t(EXTERNAL_USER_TYPE_LABEL_KEYS[type]) : EMPTY_VALUE;

const createColumns = (
  t: TFunction,
  onClickView: (externalUser: ExternalUser) => void,
  onClickDelete: (externalUser: ExternalUser) => void
): ColumnDef<ExternalUser, unknown>[] => [
  {
    id: "registeredOn",
    accessorFn: externalUser => externalUser.registered_on ?? undefined,
    header: ({ column }) => (
      <SortableColumnHeader column={column} label={t("column.registeredOn")} />
    ),
    cell: ({ row }) => (
      <span className="whitespace-nowrap text-muted-foreground">
        {formatDateTimeCompact(row.original.registered_on) || EMPTY_VALUE}
      </span>
    ),
    sortingFn: "basic",
    sortUndefined: "last",
    meta: { cellClassName: "min-w-40 px-3", headerClassName: "min-w-40 px-3" },
  },
  {
    id: "type",
    accessorFn: externalUser => getTypeLabel(t, externalUser.type),
    header: ({ column }) => (
      <SortableColumnHeader column={column} label={t("column.externalUserType")} />
    ),
    meta: { cellClassName: "min-w-44 max-w-56 px-3", headerClassName: "min-w-44 max-w-56 px-3" },
  },
  {
    id: "organization",
    accessorKey: "organization",
    header: ({ column }) => (
      <SortableColumnHeader column={column} label={t("column.institutionName")} />
    ),
    meta: {
      cellClassName: "min-w-40 whitespace-normal px-3",
      headerClassName: "min-w-40 px-3",
    },
  },
  {
    id: "fullName",
    accessorKey: "fullName",
    header: ({ column }) => (
      <SortableColumnHeader column={column} label={t("column.externalUserFullName")} />
    ),
    cell: ({ row }) => (
      <span className="font-medium text-foreground">{row.original.fullName || EMPTY_VALUE}</span>
    ),
    meta: {
      cellClassName: "min-w-48 whitespace-normal px-3",
      headerClassName: "min-w-48 px-3",
    },
  },
  {
    id: "email",
    accessorKey: "email",
    header: ({ column }) => (
      <SortableColumnHeader column={column} label={t("column.managerEmail")} />
    ),
    meta: { cellClassName: "min-w-56 px-3", headerClassName: "min-w-56 px-3" },
  },
  {
    id: "lastLogin",
    accessorFn: externalUser => externalUser.last_login ?? undefined,
    header: ({ column }) => <SortableColumnHeader column={column} label={t("column.lastLogin")} />,
    cell: ({ row }) => (
      <RelativeDateCell
        relative={formatRelativeDateTime(row.original.last_login, t)}
        absolute={formatDateTimeCompact(row.original.last_login)}
      />
    ),
    sortingFn: "basic",
    sortUndefined: "last",
    meta: { cellClassName: "min-w-40 px-3", headerClassName: "min-w-40 px-3" },
  },
  {
    id: "actions",
    header: () => <span className="sr-only">{t("common.edit")}</span>,
    enableSorting: false,
    cell: ({ row }) => (
      <div className="flex items-center justify-end gap-2">
        <WFPButton
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onClickView(row.original)}
        >
          <Pencil className="size-4" aria-hidden="true" />
          {t("common.edit")}
        </WFPButton>
        <WFPButton
          type="button"
          variant="destructive-secondary"
          size="sm"
          onClick={() => onClickDelete(row.original)}
        >
          <Trash2 className="size-4" aria-hidden="true" />
          {t("common.delete")}
        </WFPButton>
      </div>
    ),
    meta: { cellClassName: "w-0 px-3", headerClassName: "w-0 px-3" },
  },
];

export const List = ({
  isFetching,
  errorMessage = "",
  externalUsers,
  onClickView,
  onClickDelete,
  sorting,
  onSortingChange,
}: ListProps) => {
  const { t } = useTranslation();
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: PAGE_SIZE,
  });
  const columns = useMemo(
    () => createColumns(t, onClickView, onClickDelete),
    [onClickDelete, onClickView, t]
  );

  useEffect(() => {
    setPagination(currentPagination => {
      const pageCount = Math.max(1, Math.ceil(externalUsers.length / currentPagination.pageSize));
      const nextPageIndex = Math.min(currentPagination.pageIndex, pageCount - 1);

      return nextPageIndex === currentPagination.pageIndex
        ? currentPagination
        : { ...currentPagination, pageIndex: nextPageIndex };
    });
  }, [externalUsers.length]);

  useEffect(() => {
    setPagination(currentPagination =>
      currentPagination.pageIndex === 0 ? currentPagination : { ...currentPagination, pageIndex: 0 }
    );
  }, [sorting]);

  const table = useReactTable({
    columns,
    data: externalUsers,
    autoResetPageIndex: false,
    enableMultiSort: false,
    enableSortingRemoval: false,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getRowId: externalUser => String(externalUser.id),
    getSortedRowModel: getSortedRowModel(),
    onPaginationChange: setPagination,
    onSortingChange,
    state: { pagination, sorting },
  });

  if (errorMessage) {
    return (
      <Alert variant="destructive">
        <TriangleAlert />
        <AlertDescription>{errorMessage}</AlertDescription>
      </Alert>
    );
  }

  const rows = table.getRowModel().rows;
  const columnCount = table.getVisibleLeafColumns().length;

  return (
    <div className="overflow-hidden rounded-md border border-border bg-background">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map(headerGroup => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <TableHead
                  key={header.id}
                  className={getColumnMeta(header.column.columnDef)?.headerClassName}
                  aria-sort={getColumnSortAria(header.column)}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {isFetching ? (
            <TableRow>
              <TableCell colSpan={columnCount}>
                <Loading isVisible small />
              </TableCell>
            </TableRow>
          ) : null}
          {!isFetching && rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columnCount} className="h-24 text-center text-muted-foreground">
                {t("externalUsers.empty")}
              </TableCell>
            </TableRow>
          ) : null}
          {!isFetching
            ? rows.map(row => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map(cell => (
                    <TableCell
                      key={cell.id}
                      className={cn("p-3", getColumnMeta(cell.column.columnDef)?.cellClassName)}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            : null}
        </TableBody>
      </Table>
      {externalUsers.length > 0 ? <DataTablePagination table={table} /> : null}
    </div>
  );
};
